import { motion } from 'motion/react';
import { Link } from 'react-router';
import { getArtistBySlug, styleLabels, type Work } from '../data';
import { useLang } from '../hooks/useLang';

type WorkCardProps = {
  work: Work;
  index?: number;
  /** Retained for layout slot sizing in callers; visual ratio is constant. */
  size?: 'sm' | 'md' | 'lg';
};

/**
 * Editorial work card. Every work is a Link to /lavori/:id — there is no
 * modal/lightbox path. Cards on a colourful image grid bind together through
 * a uniform `ink-photo` filter and a darken-on-rest overlay that lifts on hover.
 */
export function WorkCard({ work, index }: WorkCardProps) {
  const lang = useLang();
  const artist = getArtistBySlug(work.artistSlug);
  const styleLabel = styleLabels[work.style][lang];
  const diaryLabel = lang === 'it' ? 'Diario' : 'Diary';

  return (
    <motion.div
      whileHover="hover"
      initial="rest"
      animate="rest"
      className="group relative w-full"
    >
      <Link
        to={`/lavori/${work.id}`}
        className="block w-full text-left"
        data-magnetic="true"
        aria-label={`${work.title[lang]} — ${diaryLabel}`}
      >
        <div className="relative aspect-[3/4] overflow-hidden bg-ink-line">
          <motion.img
            src={work.image}
            alt={work.alt[lang]}
            loading="lazy"
            decoding="async"
            variants={{
              rest: { scale: 1 },
              hover: { scale: 1.03 },
            }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="h-full w-full object-cover ink-photo group-hover:ink-photo-hover"
          />
          {/* Base-tint to anchor in the dark page, lifts on hover */}
          <motion.div
            aria-hidden
            variants={{ rest: { opacity: 0.18 }, hover: { opacity: 0 } }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 bg-ink-base pointer-events-none"
          />
          {/* Bottom vignette so caption can breathe */}
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-ink-base/55 via-transparent to-transparent pointer-events-none"
          />
          {/* Diary affordance — invites the entry into the long-form page */}
          <span
            aria-hidden
            className="absolute left-3 top-3 md:left-4 md:top-4 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.22em] uppercase text-ink-paper/85 bg-ink-base/55 backdrop-blur-[2px] px-2 py-1"
          >
            <span className="inline-block size-1 rounded-full bg-ink-accent" />
            {diaryLabel}
          </span>
        </div>
        <div className="mt-5 flex items-baseline justify-between gap-4 border-t border-ink-line/40 pt-4">
          <div className="min-w-0">
            <p className="font-display text-lg md:text-xl tracking-display leading-tight truncate lowercase">
              {work.title[lang]}
            </p>
            <p className="mt-1.5 font-display text-sm text-ink-muted lowercase tracking-tight">
              {artist?.name.toLowerCase()} · {work.year} · {styleLabel.toLowerCase()}
            </p>
          </div>
          <span
            aria-hidden
            className="shrink-0 font-mono text-[10px] tracking-[0.2em] text-ink-muted/50"
          >
            {index !== undefined ? `0${index + 1}`.slice(-2) : ''}
          </span>
        </div>
      </Link>
    </motion.div>
  );
}
